import { APP_PATHS } from '@/constant/app-paths';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { MenuItem, SettingsSection } from '@/types/sidebar.type';

/** Each settings page opens with its own page permission (Phase 8). */
const S = HRM_PERMISSIONS.SETTINGS;

/** A settings page that is not built yet: everyone who may open Settings sees it, as "Soon". */
const soon = (id: string, name: string, path: string): MenuItem => ({ id, name, path, soon: true });

/**
 * The Settings area (`/settings`), grouped as in the HR Module Navigation Map: each group sets up one
 * module. Opened from the gear in the sidebar footer, not from the main menu.
 *
 * Items marked "until ..." are pages that a later phase merges into another page's tab.
 */
export const settingsSections: SettingsSection[] = [
  {
    title: 'ORGANIZATION',
    description: 'SETTINGS_ORGANIZATION_DESCRIPTION',
    scope: 'COMPANY',
    items: [
      {
        id: 'hr-department-manager',
        name: 'DEPARTMENTS',
        path: APP_PATHS.DEPARTMENTS,
        permissions: [S.VIEW_DEPARTMENTS],
      },
      {
        id: 'hr-designation-manager',
        name: 'DESIGNATIONS',
        path: APP_PATHS.DESIGNATIONS,
        permissions: [S.VIEW_DESIGNATIONS],
      },
      {
        id: 'hr-location-manager',
        name: 'LOCATIONS',
        path: APP_PATHS.LOCATIONS,
        permissions: [S.VIEW_LOCATIONS],
      },
      // Kosovo Projects and MBM Projects tabs (Phase 8).
      {
        id: 'hr-project-manager',
        name: 'PROJECTS',
        path: APP_PATHS.PROJECTS,
        permissions: [S.VIEW_PROJECTS],
      },
      soon('settings-documents', 'DOCUMENTS', APP_PATHS.DOCUMENTS),
    ],
  },
  {
    title: 'COMPENSATION',
    description: 'SETTINGS_COMPENSATION_DESCRIPTION',
    scope: 'COMPANY',
    items: [
      {
        id: 'hr-ctc-level-manager',
        name: 'CTC_LEVELS',
        path: APP_PATHS.CTC_LEVELS,
        permissions: [S.VIEW_CTC_LEVELS],
      },
    ],
  },
  {
    title: 'ACCESS_AND_APPROVALS',
    description: 'SETTINGS_ACCESS_AND_APPROVALS_DESCRIPTION',
    scope: 'COMPANY',
    items: [
      {
        id: 'hr-role-manager',
        name: 'ROLES',
        path: APP_PATHS.ROLES,
        permissions: [S.VIEW_ROLES],
      },
      soon('settings-approvals', 'APPROVALS', APP_PATHS.APPROVALS),
    ],
  },
  {
    title: 'SYSTEM',
    description: 'SETTINGS_SYSTEM_DESCRIPTION',
    scope: 'COMPANY',
    items: [
      {
        id: 'hr-logs',
        name: 'AUDIT_LOG',
        path: APP_PATHS.AUDIT_LOG,
        permissions: [HRM_PERMISSIONS.SETTINGS.AUDIT_LOG_VIEW],
      },
    ],
  },
  {
    title: 'TIME_OFF',
    description: 'SETTINGS_TIME_OFF_DESCRIPTION',
    scope: 'MODULES',
    items: [
      {
        id: 'hr-leave-builder',
        name: 'LEAVE_POLICIES',
        path: APP_PATHS.LEAVE_POLICIES,
        permissions: [S.VIEW_LEAVE_POLICIES],
      },
      {
        id: 'hr-holiday',
        name: 'HOLIDAYS',
        path: APP_PATHS.HOLIDAYS,
        permissions: [S.VIEW_HOLIDAYS],
      },
      soon('settings-leave-rules', 'LEAVE_RULES', APP_PATHS.LEAVE_RULES),
    ],
  },
  {
    title: 'TIME_AND_ATTENDANCE',
    description: 'SETTINGS_TIME_AND_ATTENDANCE_DESCRIPTION',
    scope: 'MODULES',
    items: [
      soon('settings-work-schedules', 'WORK_SCHEDULES', APP_PATHS.WORK_SCHEDULES),
      {
        id: 'settings-attendance-rules',
        name: 'ATTENDANCE_RULES',
        path: APP_PATHS.ATTENDANCE_RULES,
        permissions: [S.VIEW_ATTENDANCE_RULES],
      },
      soon('settings-timesheet-rules', 'TIMESHEET_RULES', APP_PATHS.TIMESHEET_RULES),
    ],
  },
  {
    title: 'PERFORMANCE',
    description: 'SETTINGS_PERFORMANCE_DESCRIPTION',
    scope: 'MODULES',
    items: [
      {
        id: 'hr-form-creation',
        name: 'REVIEW_TEMPLATES',
        path: APP_PATHS.REVIEW_TEMPLATES,
        permissions: [S.VIEW_REVIEW_TEMPLATES],
      },
      {
        id: 'hr-kpi-configuration',
        name: 'KPIS',
        path: APP_PATHS.KPIS,
        permissions: [HRM_PERMISSIONS.SETTINGS.KPIS_CONFIGURE],
      },
      soon('settings-review-rules', 'REVIEW_RULES', APP_PATHS.REVIEW_RULES),
      {
        id: 'hr-skill-manager',
        name: 'SKILLS',
        path: APP_PATHS.SKILLS,
        permissions: [S.VIEW_SKILLS],
      },
    ],
  },
  {
    title: 'SURVEYS',
    description: 'SETTINGS_SURVEYS_DESCRIPTION',
    scope: 'MODULES',
    items: [
      {
        id: 'hr-survey-form-creation',
        name: 'SURVEY_FORMS',
        path: APP_PATHS.SURVEY_FORMS,
        permissions: [S.VIEW_SURVEY_FORMS],
      },
    ],
  },
  {
    title: 'MEALS',
    description: 'SETTINGS_MEALS_DESCRIPTION',
    scope: 'MODULES',
    items: [
      // The former Food Menu tab of Meal Orders (Meal phase, 2 Nov 2026).
      {
        id: 'settings-meal-menu',
        name: 'MEAL_MENU',
        path: APP_PATHS.MEAL_MENU,
        permissions: [HRM_PERMISSIONS.MEAL.LUNCH_VIEW_ALL],
      },
      {
        id: 'settings-meal-rules',
        name: 'MEAL_RULES',
        path: APP_PATHS.MEAL_RULES,
        permissions: [HRM_PERMISSIONS.MEAL.LUNCH_VIEW_ALL],
      },
    ],
  },
  {
    title: 'SERVICE_REQUESTS',
    description: 'SETTINGS_SERVICE_REQUESTS_DESCRIPTION',
    scope: 'MODULES',
    items: [
      soon('settings-request-types', 'REQUEST_TYPES', APP_PATHS.REQUEST_TYPES),
      {
        id: 'hr-noc-type-manager',
        name: 'NOC_TYPES',
        path: APP_PATHS.NOC_TYPES,
        permissions: [S.VIEW_NOC_TYPES],
      },
    ],
  },
];

/** The scopes of the Settings overview, in order. */
export const SETTINGS_SCOPES = ['COMPANY', 'MODULES'] as const;

/** Every built settings page's permissions: holding any of them opens Settings (and its Soon pages). */
export const SETTINGS_ACCESS_PERMISSIONS: string[] = [
  ...new Set(
    settingsSections
      .flatMap((section) => section.items)
      .flatMap((item) => (item.permissions === undefined ? [] : [item.permissions].flat()))
  ),
];

/** The Soon pages, for their routes. */
export const SOON_SETTINGS_PAGES: MenuItem[] = settingsSections
  .flatMap((section) => section.items)
  .filter((item) => item.soon);
